/**
 * The two strings the shell shows for whoever is signed in.
 *
 * The persona (a demo tweak) used to be the only identity the shell knew. With a
 * real login the signed-in account takes precedence: a client seeing a
 * presenter's persona name in the top bar of their own session is the kind of
 * thing that ends a demo. The persona still drives the rest of the tweaks.
 */

import type { AuthUser } from './context';

export interface ShellIdentity {
  readonly displayName: string;
  readonly initials: string;
}

/** Up to two initials from a name, or from the local part of an email. */
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/[\s._-]+/)
    .filter((w) => w.length > 0);
  if (words.length === 0) return '?';
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/** The identity to show: the signed-in user when there is one, else the fallback. */
export function shellIdentity(user: AuthUser | null, fallback: ShellIdentity): ShellIdentity {
  if (user === null) return fallback;
  const name = user.displayName ?? user.email;
  if (name === null || name.trim() === '') return fallback;
  const local = name.includes('@') ? (name.split('@')[0] ?? name) : name;
  return { displayName: name, initials: initialsOf(local) };
}
